<template>
    <!-- ONE stable DOM shape (R6a) — the previous v-if/v-else swapped between
         a capture-wrapper subtree and a bare slot, so a click landing during
         a billing-state flip could die (user report + two silent no-op
         clicks in the landing e2e). The wrapper is now ALWAYS present;
         `maybeIntercept` consults blocked-state AT CLICK TIME and only then
         stops the event and opens a dialog. -->
    <div class="contents" @click.capture="maybeIntercept">
        <slot />
    </div>

    <BuyCreditsDialog v-model:visible="showBuyCreditsDialog" />
    <BuyAssessmentsDialog v-model:visible="showBuyAssessmentsDialog" />
    <PlanSwitchDialog v-model:visible="showPlanSwitchDialog" />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useFeaturesStore } from '@/stores/features'
import BuyCreditsDialog from '@/components/settings/billing/BuyCreditsDialog.vue'
import BuyAssessmentsDialog from '@/components/settings/billing/BuyAssessmentsDialog.vue'
import PlanSwitchDialog from '@/components/settings/billing/PlanSwitchDialog.vue'
import { useSelfServeGate } from '@/composables/useSelfServeGate'
import { useCreditGateBlocked } from '@/composables/useCreditGateBlocked'

interface Props {
    /** Action key matching credit_config/estimates (e.g. "assessment_per_requirement", "internal_audit_phase") */
    action: string
    /** Skip the credit check (e.g., when IA has already consumed AI quota) */
    bypass?: boolean
    /** Unit count for per-unit actions (e.g. number of regulations for regulation_watch_per_norm) */
    count?: number
}

const props = withDefaults(defineProps<Props>(), {
    bypass: false,
    count: undefined,
})

const featuresStore = useFeaturesStore()
const { canSelfServeTopup, shouldContactSalesForPlanSwitch, openContactSales } = useSelfServeGate()

const showBuyCreditsDialog = ref(false)
const showBuyAssessmentsDialog = ref(false)
const showPlanSwitchDialog = ref(false)

// `props` is passed directly (not destructured into a fresh literal) so this
// stays reactive to `bypass` changing at runtime.
const isBlocked = useCreditGateBlocked(props)

function maybeIntercept(event: MouseEvent): void {
    if (!isBlocked.value) return
    event.stopPropagation()
    event.preventDefault()
    openDialog()
}

function openDialog() {
    // Try plans upgrade via the plan-switch dialog — but an existing tenant never
    // self-serves a plan change while gated (MYAC-837), so route to Contact sales
    // before the picker (and its prices) can open.
    if (featuresStore.isTryPlan) {
        if (shouldContactSalesForPlanSwitch()) {
            openContactSales('upgrade')
            return
        }
        showPlanSwitchDialog.value = true
        return
    }
    // Credit top-up / assessment purchase are self-serve paid buys — gate them:
    // route to Contact sales unless self-serve is on or the tenant is allowed.
    if (!canSelfServeTopup()) {
        openContactSales('topup')
        return
    }
    if (featuresStore.isCreditPlan) {
        showBuyCreditsDialog.value = true
    } else {
        showBuyAssessmentsDialog.value = true
    }
}
</script>
